/*
 * Payslip PDF generator.
 *
 * Produces a single-page A4 payslip for one employee / one month,
 * driven by the payroll breakdown object computed by payrollMath.js
 * (regularPay, overtimePay, holiday buckets, extraEarnings[],
 *  SSS/PhilHealth/Pag-IBIG/Insurance/otherDeductions[], netPay).
 *
 * Layout:
 *   - Gold header band with store name + "PAYSLIP" title + pay period.
 *   - Employee info card with profile photo (left) and two-column
 *     detail grid (right): name, code, role, department, store, status,
 *     phone, email, days worked.
 *   - Two side-by-side tables: EARNINGS and DEDUCTIONS.
 *   - NET PAY highlight bar + amount in words.
 *   - Footer with generation timestamp and signature lines.
 *
 * The file is self-contained — it carries its own pdfSafe/loadImage/
 * currentStoreName helpers rather than importing from ReportsPage so
 * there's no circular dependency across feature folders.
 */

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getStores } from "../../utils/stores";
import { currentSchoolLetterhead } from "../../utils/currentSchool";

const GOLD  = [193, 148, 28];
const LGOLD = [255, 248, 220];
const DARK  = [40, 40, 40];
const GRAY  = [100, 100, 100];

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

// ── helpers ──────────────────────────────────────────────────────────

/** jsPDF's Helvetica is Latin-1 only — strip/replace common offenders. */
function pdfSafe(s) {
  if (s == null) return "";
  return String(s)
    .replace(/₱/g, "P")
    .replace(/[—–]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\xFF]/g, "");
}

/** Preload an image URL so jsPDF can embed it synchronously. */
function loadImage(url) {
  return new Promise(resolve => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function peso(n) {
  return `P${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

/**
 * Resolve the letterhead for this payslip.
 *
 * Multi-tenant priority order:
 *   1. The currently-selected school via {@link currentSchoolLetterhead}.
 *      A branch user is locked to one school; a chain admin's selected
 *      school in the topbar drives the letterhead so a payslip
 *      generated while context-switched to NCLA Cebu carries NCLA
 *      Cebu's letterhead — not "the chain default."
 *   2. Falls back to the legacy "stores" config if the user session has
 *      no school field (older logins predating Phase 2A).
 */
function currentStoreName() {
  try {
    const letterhead = currentSchoolLetterhead();
    if (letterhead && letterhead.primary) {
      return { name: letterhead.primary, subtitle: letterhead.secondary || "" };
    }
  } catch { /* fall through to legacy stores */ }
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    const u = raw ? JSON.parse(raw) : null;
    const id = localStorage.getItem(`currentStore_${u?.id ?? "guest"}`);
    const stores = getStores();
    const match = stores.find(s => String(s.id) === String(id)) || stores[0];
    return { name: match?.name || "", subtitle: match?.subtitle || "" };
  } catch {
    return { name: "", subtitle: "" };
  }
}

// Number-in-words (English) — covers up to 999,999,999.99 which is
// vastly more than any single-month payslip will ever produce.
const ONES = [
  "Zero","One","Two","Three","Four","Five","Six","Seven","Eight","Nine",
  "Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen",
  "Seventeen","Eighteen","Nineteen",
];
const TENS = ["", "", "Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];

function under1000(n) {
  if (n < 20) return ONES[n];
  if (n < 100) {
    const t = Math.floor(n / 10), r = n % 10;
    return TENS[t] + (r ? " " + ONES[r] : "");
  }
  const h = Math.floor(n / 100), r = n % 100;
  return ONES[h] + " Hundred" + (r ? " " + under1000(r) : "");
}

function amountInWords(n) {
  const whole = Math.floor(Math.abs(Number(n) || 0));
  const cents = Math.round((Math.abs(Number(n) || 0) - whole) * 100);
  if (whole === 0 && cents === 0) return "Zero Pesos";

  const groups = ["", "Thousand", "Million", "Billion"];
  let i = 0, result = "";
  let x = whole;
  while (x > 0) {
    const chunk = x % 1000;
    if (chunk) {
      const part = under1000(chunk) + (groups[i] ? " " + groups[i] : "");
      result = part + (result ? " " + result : "");
    }
    x = Math.floor(x / 1000);
    i++;
  }
  if (!result) result = "Zero";
  let out = result + " Peso" + (whole === 1 ? "" : "s");
  if (cents > 0) out += " and " + under1000(cents) + " Centavo" + (cents === 1 ? "" : "s");
  return out;
}

// ── layout primitives ────────────────────────────────────────────────

function header(doc, store, subtitle, monthLabel) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, pageW, 32, "F");

  doc.setTextColor(255, 255, 255);

  // Store name (small caps, top)
  doc.setFontSize(10); doc.setFont(undefined, "bold");
  doc.text(pdfSafe(store || "").toUpperCase(), 14, 10);
  if (subtitle) {
    doc.setFontSize(8); doc.setFont(undefined, "normal");
    doc.text(pdfSafe(subtitle), 14, 15);
  }

  // Payslip title + pay period
  doc.setFontSize(20); doc.setFont(undefined, "bold");
  doc.text("PAYSLIP", 14, 26);

  doc.setFontSize(10); doc.setFont(undefined, "normal");
  doc.text(`Pay Period: ${pdfSafe(monthLabel)}`, pageW - 14, 26, { align: "right" });

  doc.setTextColor(0, 0, 0);
  return 40; // y cursor
}

function sectionLabel(doc, text, y) {
  doc.setFontSize(9); doc.setFont(undefined, "bold");
  doc.setTextColor(...GRAY);
  doc.text(text.toUpperCase(), 14, y);
  doc.setTextColor(0, 0, 0);
  return y + 4;
}

// ── main export ──────────────────────────────────────────────────────

/**
 * Generate and download a payslip PDF.
 *
 * @param {object} employee   Full employee profile object (same shape
 *                            SalaryCenter already receives as
 *                            `selectedEmployee`).
 * @param {object} payroll    Result of computePayroll() — all the pay
 *                            buckets, deductions, and totals.
 * @param {number} month      0-based month (matches Date#getMonth()).
 * @param {number} year       4-digit year.
 */
export async function exportPayslipPDF(employee, payroll, month, year) {
  // Refuse to generate a payslip for an employee whose base salary
  // isn't configured. Even though the Salary page's button is gated on
  // this condition too, a payslip showing ₱0 (or worse, the legacy
  // ₱700/day fallback that produced a phantom ₱15,400 net pay) printed
  // on PDF letterhead is the kind of mistake that doesn't get caught
  // before it's signed. Better to halt here than to print fiction.
  const monthlySalary = Number(employee?.salary);
  if (!Number.isFinite(monthlySalary) || monthlySalary <= 0) {
    if (typeof window !== "undefined" && typeof window.alert === "function") {
      window.alert(
        "Cannot generate payslip: base salary is not configured for this employee.\n\n" +
        "Open People → Edit Employee, set the Salary field, and try again."
      );
    }
    return;
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  const store = currentStoreName();
  const monthLabel = `${MONTHS[month] || ""} ${year}`;

  let y = header(doc, store.name, store.subtitle, monthLabel);

  // ── Employee info card ─────────────────────────────────────────
  const cardTop = y + 2;
  const cardH   = 44;
  doc.setDrawColor(220, 220, 220);
  doc.setFillColor(252, 250, 245);
  doc.roundedRect(14, cardTop, pageW - 28, cardH, 3, 3, "FD");

  // Profile photo on the left (square 30mm)
  const photo = await loadImage(employee?.photoUrl);
  const photoX = 18, photoY = cardTop + 7, photoSize = 30;
  if (photo) {
    try { doc.addImage(photo, "JPEG", photoX, photoY, photoSize, photoSize); } catch {}
  } else {
    // Placeholder circle with initials
    doc.setFillColor(...LGOLD);
    doc.rect(photoX, photoY, photoSize, photoSize, "F");
    doc.setTextColor(...GOLD);
    doc.setFontSize(16); doc.setFont(undefined, "bold");
    const initials = (employee?.name || "?")
      .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
    doc.text(initials, photoX + photoSize / 2, photoY + photoSize / 2 + 3, { align: "center" });
    doc.setTextColor(0, 0, 0);
  }

  // Two-column detail grid to the right of the photo.
  // Column A: Name / Role / Department / Store
  // Column B: Code / Status / Phone / Email
  const detailsX = photoX + photoSize + 6;
  const colWidth = (pageW - 28 - (photoSize + 6) - 10) / 2;
  const colAX    = detailsX;
  const colBX    = detailsX + colWidth + 4;

  const info = Array.isArray(employee?.info) ? employee.info : [];
  const pick = (k) => {
    const row = info.find(i => i.k === k);
    return row?.v || "";
  };

  const rows = [
    ["Employee Name", employee?.name || "—",
     "Employee Code", employee?.code || "—"],
    ["Role",          employee?.role || "—",
     "Status",        employee?.status || "—"],
    ["Department",    employee?.department || "—",
     "Phone",         String(pick("Phone number")).replace(/^\+?/, "")],
    ["Store",         employee?.storeName || "—",
     "Email",         pick("Email")],
  ];

  let ry = cardTop + 10;
  const rowH = 7;
  doc.setFontSize(8);
  for (const [labA, valA, labB, valB] of rows) {
    doc.setTextColor(...GRAY);
    doc.setFont(undefined, "normal");
    doc.text(pdfSafe(labA), colAX, ry);
    doc.text(pdfSafe(labB), colBX, ry);

    doc.setTextColor(...DARK);
    doc.setFont(undefined, "bold");
    doc.text(pdfSafe(valA), colAX, ry + 3.5, { maxWidth: colWidth });
    doc.text(pdfSafe(valB), colBX, ry + 3.5, { maxWidth: colWidth });

    ry += rowH;
  }
  doc.setFont(undefined, "normal");
  doc.setTextColor(0, 0, 0);

  y = cardTop + cardH + 6;

  // ── Pay Period summary strip ───────────────────────────────────
  doc.setFillColor(...LGOLD);
  doc.rect(14, y, pageW - 28, 10, "F");
  doc.setFontSize(9); doc.setFont(undefined, "bold");
  doc.setTextColor(...GRAY);
  doc.text("PAY PERIOD", 18, y + 6);
  doc.setTextColor(...DARK);
  doc.text(pdfSafe(monthLabel), 50, y + 6);
  doc.setTextColor(...GRAY);
  doc.text("DAYS WORKED", 100, y + 6);
  doc.setTextColor(...DARK);
  doc.text(String(payroll.daysWorked || 0), 130, y + 6);
  doc.setTextColor(...GRAY);
  doc.text("DAILY RATE", 150, y + 6);
  doc.setTextColor(...DARK);
  doc.text(peso(payroll.dailyRate), 172, y + 6);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, "normal");
  y += 14;

  // ── Leave Credits strip ────────────────────────────────────────
  // Pulled either from the profile's parsed `leaves` array or from
  // the raw leavesJson on the employee record — whichever is
  // available. Each entry: { name, days, convertible }.
  let leaves = Array.isArray(employee?.leaves) ? employee.leaves : null;
  if (!leaves) {
    try {
      const raw = employee?.leavesJson;
      const parsed = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : [];
      leaves = Array.isArray(parsed) ? parsed : [];
    } catch { leaves = []; }
  }
  if (leaves.length > 0) {
    sectionLabel(doc, "Leave Credits", y);
    autoTable(doc, {
      startY: y + 2,
      head: [["Leave Type", "Days", "Convertible"]],
      body: leaves.map(l => [
        pdfSafe(l.name || ""),
        String(Number(l.days) || 0),
        l.convertible ? "Yes" : "No",
      ]),
      headStyles: { fillColor: GOLD, textColor: 255, fontStyle: "bold", fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: DARK },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 30, halign: "right" },
        2: { cellWidth: 30, halign: "center" },
      },
      margin: { left: 14, right: 14 },
      theme: "grid",
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ── Earnings + Deductions side-by-side ─────────────────────────
  // Build the two row lists from the payroll breakdown. Every
  // standard bucket is always listed — even ₱0.00 lines — so the
  // payslip mirrors the on-screen Payroll Computation card exactly
  // and the recipient can see every category that was considered.
  const earningsRows = [
    ["Regular Pay",             peso(payroll.regularPay)],
    ["Overtime (+25%/hr)",      peso(payroll.overtimePay)],
    ["Special Holiday (+30%)",  peso(payroll.specialHolidayPay)],
    ["Regular Holiday (+100%)", peso(payroll.regularHolidayPay)],
  ];
  (payroll.extraEarnings || []).forEach(o => {
    earningsRows.push([pdfSafe(o.name), peso(o.value)]);
  });

  const deductionsRows = [
    ["Late Deduction",      peso(payroll.lateDeduction)],
    ["Undertime Deduction", peso(payroll.undertimeDeduction)],
    ["Absent Deduction",    peso(payroll.absentDeduction)],
    [`Days Absent`,         String(payroll.daysAbsent || 0)],
    ["SSS",                 peso(payroll.sssDeduction)],
    ["PhilHealth",          peso(payroll.philhealthDeduction)],
    ["Pag-IBIG",            peso(payroll.pagibigDeduction)],
    ["Insurance",           peso(payroll.insuranceDeduction)],
  ];
  (payroll.otherDeductions || []).forEach(o => {
    deductionsRows.push([pdfSafe(o.name), peso(o.value)]);
  });

  // Left table: Earnings.
  sectionLabel(doc, "Earnings", y);
  autoTable(doc, {
    startY: y + 2,
    head: [["Description", "Amount"]],
    body: earningsRows,
    foot: [[
      { content: "Total Earnings",           styles: { fontStyle: "bold", fillColor: LGOLD, textColor: DARK } },
      { content: peso(payroll.gross || 0),   styles: { fontStyle: "bold", fillColor: LGOLD, textColor: DARK, halign: "right" } },
    ]],
    headStyles: { fillColor: GOLD, textColor: 255, fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: DARK },
    footStyles: { textColor: DARK },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 30, halign: "right" },
    },
    margin: { left: 14, right: pageW / 2 + 2 },
    theme: "grid",
  });
  const leftFinalY = doc.lastAutoTable.finalY;

  // Right table: Deductions.
  // Section label for the right column (aligned with Earnings label y).
  doc.setFontSize(9); doc.setFont(undefined, "bold");
  doc.setTextColor(...GRAY);
  doc.text("DEDUCTIONS", pageW / 2 + 2, y);
  doc.setTextColor(0, 0, 0);

  autoTable(doc, {
    startY: y + 2,
    head: [["Description", "Amount"]],
    body: deductionsRows,
    foot: [[
      { content: "Total Deductions",                   styles: { fontStyle: "bold", fillColor: LGOLD, textColor: DARK } },
      { content: peso(payroll.totalDeductions || 0),   styles: { fontStyle: "bold", fillColor: LGOLD, textColor: DARK, halign: "right" } },
    ]],
    headStyles: { fillColor: GOLD, textColor: 255, fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: DARK },
    footStyles: { textColor: DARK },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 30, halign: "right" },
    },
    margin: { left: pageW / 2 + 2, right: 14 },
    theme: "grid",
  });
  const rightFinalY = doc.lastAutoTable.finalY;

  y = Math.max(leftFinalY, rightFinalY) + 8;

  // ── Net Pay bar ────────────────────────────────────────────────
  doc.setFillColor(...GOLD);
  doc.roundedRect(14, y, pageW - 28, 16, 2, 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11); doc.setFont(undefined, "bold");
  doc.text("NET PAY", 18, y + 10);
  doc.setFontSize(16);
  doc.text(peso(payroll.netPay || 0), pageW - 18, y + 10.5, { align: "right" });
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, "normal");
  y += 20;

  // Amount in words
  doc.setFontSize(9); doc.setFont(undefined, "italic");
  doc.setTextColor(...GRAY);
  const words = amountInWords(payroll.netPay || 0);
  doc.text(`In words: ${pdfSafe(words)}`, 14, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, "normal");
  y += 10;

  // ── Signatures ────────────────────────────────────────────────
  const sigY    = y + 20;
  const lineLen = 60;
  const leftSigX  = 18;
  const rightSigX = pageW - 18 - lineLen;
  doc.setDrawColor(120, 120, 120);
  doc.line(leftSigX,  sigY, leftSigX  + lineLen, sigY);
  doc.line(rightSigX, sigY, rightSigX + lineLen, sigY);

  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text("Employer Signature",  leftSigX,  sigY + 5);
  doc.text("Employee Signature", rightSigX, sigY + 5);

  // ── Footer ────────────────────────────────────────────────────
  const footY = doc.internal.pageSize.getHeight() - 10;
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  const genAt = new Date().toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
  doc.text(`Generated ${pdfSafe(genAt)}  •  This is a system generated payslip`, pageW / 2, footY, { align: "center" });

  // Save — filename: "payslip_<code>_<year>-<month>.pdf"
  const safeCode = (employee?.code || "employee").replace(/[^a-z0-9_-]/gi, "_");
  const fileMonth = String(month + 1).padStart(2, "0");
  doc.save(`payslip_${safeCode}_${year}-${fileMonth}.pdf`);
}
